class ModalButton extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.addEventListener("click", this._handleClick.bind(this), {
      signal: this._abortController.signal
    });
    if (!document.getElementById(this.dataset.modal)?.querySelector(":scope > dialog")) return;
    this.setAttribute("role", "button");
    this.tabIndex = 0;
    this.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      this.click();
    }, { signal: this._abortController.signal });
  }
  _handleClick(event){
    const modalID = this.getAttribute("data-modal");
    const modal = document.getElementById(modalID);
    if (this.dataset.video) this._loadVideo(modal);
    const videoMedia = modal?.querySelector("video-media");

    if (videoMedia) videoMedia.click();

    if(modalID) {
      document.dispatchEvent(new CustomEvent("modal:open", {
        detail: {id: modalID}
      }));
    }
  }
  _loadVideo(modal) {
    const slot = modal?.querySelector("[data-video-slot]");
    const match = this.dataset.video.match(/(?:youtu\.be\/|[?&]v=|shorts\/|embed\/)([\w-]{11})|vimeo\.com\/(?:video\/)?(\d+)/);
    if (!slot || !match) return;
    const media = document.createElement("video-media");
    const template = document.createElement("template");
    const iframe = document.createElement("iframe");
    media.setAttribute("host", match[1] ? "youtube" : "vimeo");
    iframe.src = match[1]
      ? `https://www.youtube.com/embed/${match[1]}?playsinline=1&playlist=${match[1]}&enablejsapi=1&rel=0&modestbranding=1&origin=${encodeURIComponent(location.origin)}`
      : `https://player.vimeo.com/video/${match[2]}?autopause=1&transparent=0&responsive=1&portrait=0&title=0&byline=0`;
    iframe.title = this.dataset.videoTitle || "";
    iframe.allow = "autoplay; encrypted-media";
    iframe.allowFullscreen = true;
    template.content.append(iframe);
    media.append(template);
    slot.replaceChildren(media);
    const popover = this.closest("[popover]");
    if (popover) {
      document.querySelector(`[popovertarget="${popover.id}"]`)?.focus();
      popover.hidePopover();
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
};
if (!window.customElements.get("modal-button")) {
  window.customElements.define("modal-button", ModalButton);
}

class ModalContents extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;
    document.addEventListener("modal:open", this._handleOpen.bind(this), { signal });
    document.addEventListener("modal:close", this._handleClose.bind(this),{ signal });
    this.addEventListener("click", this._handleBackdropClick.bind(this), { signal });
  }
  get _id() {
    return this.getAttribute("id");
  }
  get _dialog() {
    return this.querySelector(":scope > dialog");
  }
  _handleOpen(event){
    if (event.detail.id !== this._id) return;
    const dialog = this._dialog;
    if (dialog) {
      dialog.showModal();
      dialog.addEventListener("close", () => this._handleClose({ detail: { id: this._id } }), { once: true });
    } else {
      this.classList.add("active-modal");
    }
    document.body.classList.add('overflow-hidden');
  }
  _handleClose(event){
    if (event.detail.id !== this._id) return;
    this._dialog?.close();
    this.classList.remove("active-modal");
    document.body.classList.remove("overflow-hidden");
    this._pauseMedia();
  }
  _handleBackdropClick(event) {
    if (event.target === this || event.target === this._dialog || event.target.closest(".modal-close")) {
      document.dispatchEvent(new CustomEvent("modal:close", {
        detail: { id: this._id }
      }));
    }
  }
  _pauseMedia() {
    this.querySelectorAll('video-media, video, iframe').forEach((el) => {
      const tag = el.tagName.toLowerCase();
      if (tag === 'video-media') {
        el.pause?.();
      } else if (tag === 'video') {
        el.pause();
      } else if (tag === 'iframe') {
        el.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
          '*'
        );
      }
    });
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
};
if (!window.customElements.get("modal-contents")) {
  window.customElements.define("modal-contents", ModalContents);
}

class PopupTrigger extends HTMLElement {
  connectedCallback() {
    const delay = parseFloat(this.getAttribute('delay') || '0') * 1000;
    this._timer = setTimeout(() => {
      document.dispatchEvent(new CustomEvent('modal:open', {
        detail: { id: this.querySelector('modal-contents')?.getAttribute('id') }
      }));
    }, delay);
  }

  disconnectedCallback() {
    clearTimeout(this._timer);
  }
}
if (!window.customElements.get('popup-trigger')) {
  window.customElements.define('popup-trigger', PopupTrigger);
}

class InfoPopover extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this._trigger = this.querySelector(":scope > [popovertarget]");
    this._panel = this.querySelector(":scope > [popover]");
    if (!this._trigger || !this._panel) return;
    this._panel.addEventListener("beforetoggle", (event) => {
      if (event.newState === "open") this._panel.style.visibility = "hidden";
    }, { signal: this._abortController.signal });
    this._panel.addEventListener("toggle", this._handleToggle.bind(this), { signal: this._abortController.signal });
  }
  _handleToggle(event) {
    this._openController?.abort();
    if (event.newState !== "open") return;
    this._openController = new AbortController();
    const close = () => this._panel.hidePopover();
    window.addEventListener("scroll", close, { passive: true, signal: this._openController.signal });
    window.addEventListener("resize", close, { signal: this._openController.signal });
    const trigger = this._trigger.getBoundingClientRect(), gap = 8;
    const below = window.innerHeight - trigger.bottom - gap * 2, above = trigger.top - gap * 2;
    const useBelow = this._panel.scrollHeight <= below || below >= above;
    this._panel.style.maxHeight = `${Math.max(useBelow ? below : above, 0)}px`;
    this._panel.style.top = `${useBelow ? trigger.bottom + gap : Math.max(gap, trigger.top - gap - this._panel.offsetHeight)}px`;
    this._panel.style.left = `${Math.max(gap, Math.min(trigger.left, window.innerWidth - this._panel.offsetWidth - gap))}px`;
    this._panel.style.visibility = "";
  }
  disconnectedCallback() {
    this._abortController?.abort();
    this._openController?.abort();
  }
}
if (!window.customElements.get("info-popover")) {
  window.customElements.define("info-popover", InfoPopover);
}

class FaqButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener("click", this._handleClick.bind(this));
  }
  _handleClick(event){
    const containerID = this.getAttribute("data-levels");
    const detailsEl = document.getElementById(containerID);
    if (detailsEl) {
      detailsEl.open = true;
      detailsEl.scrollIntoView({
        behavior: 'smooth'
      })
    }
  }
};
if (!window.customElements.get("faq-button")) {
  window.customElements.define("faq-button", FaqButton);
}

class AccessibleCarousel extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this._paused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.querySelector('[data-media-toggle]')) this.toggleAttribute('media-paused', this._paused);

    this.addEventListener('carousel:select', (event) => {
      this._setCurrent(event.detail.slide, true);
      const status = this.querySelector('[data-carousel-status]');
      if (status) status.textContent = event.detail.slide?.getAttribute('aria-label') || '';
    }, { signal });
    this.addEventListener('media:play', this._onMediaPlay.bind(this), { signal });
    this.addEventListener('click', this._onClick.bind(this), { signal });
    if (this.hasAttribute('mouse-drag')) this._enableDrag(signal);

    const carousel = this.carousel;
    if (carousel) {
      customElements.whenDefined('scroll-carousel').then(() => {
        this._setCurrent(carousel.selectedSlide);
        if (carousel.items.filter((item) => !item.hidden).length < 2) {
          this.querySelectorAll('button[is="prev-button"], button[is="next-button"]').forEach((button) => button.disabled = true);
        }
      });
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get carousel() {
    return this.querySelector('scroll-carousel');
  }
  _setCurrent(slide, play = false) {
    if (!slide || !this.carousel) return;
    this._current = slide;
    this.carousel.items.forEach((item) => {
      item.inert = item !== slide;
      if (item !== slide) item.querySelector('video-media')?.pause();
    });
    const autoplay = slide.querySelector('video-media[autoplay]');
    this.querySelectorAll('button[data-media-toggle]').forEach((button) => button.setAttribute('aria-disabled', !autoplay));
    if (this._paused) autoplay?.pause();
    else if (play) autoplay?.play();
  }
  _onMediaPlay(event) {
    const media = event.target;
    if (!media.hasAttribute('autoplay')) return;
    if (this._paused || (this._current && !this._current.contains(media))) media.pause();
  }
  _enableDrag(signal) {
    let drag = null;
    let suppressClick = false;
    this.addEventListener('pointerdown', (event) => {
      suppressClick = false;
      const carousel = this.carousel;
      if (event.pointerType !== 'mouse' || event.button !== 0 || !carousel?.contains(event.target) || carousel.visibleItems.length < 2) return;
      drag = { id: event.pointerId, x: event.clientX, left: carousel.scrollLeft, index: carousel.selectedIndex, moved: false };
    }, { signal });
    this.addEventListener('pointermove', (event) => {
      if (drag?.id !== event.pointerId) return;
      const deltaX = event.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(deltaX) < 6) return;
        drag.moved = true;
        this.carousel.setPointerCapture(event.pointerId);
        this.setAttribute('dragging', '');
        getSelection().removeAllRanges();
      }
      this.carousel.scrollLeft = drag.left - deltaX;
    }, { signal });
    const release = (event) => {
      if (drag?.id !== event.pointerId) return;
      const { moved, x, index } = drag;
      drag = null;
      if (!moved) return;
      suppressClick = true;
      const carousel = this.carousel, deltaX = event.clientX - x, width = Math.abs(carousel.itemOffset) || carousel.clientWidth;
      const steps = Math.abs(deltaX) < width * 0.1 ? 0 : Math.max(1, Math.round(Math.abs(deltaX) / width));
      const visible = carousel.visibleItems, from = visible.indexOf(carousel.items[index]);
      const to = Math.min(Math.max(from + (deltaX < 0 ? steps : -steps), 0), visible.length - 1);
      carousel.select(carousel.items.indexOf(visible[to]), { force: true });
      const done = () => this.removeAttribute('dragging');
      carousel.addEventListener('scrollend', done, { once: true, signal });
      setTimeout(done, 1000);
    };
    this.addEventListener('pointerup', release, { signal });
    this.addEventListener('pointercancel', release, { signal });
    this.addEventListener('dragstart', (event) => this.carousel?.contains(event.target) && event.preventDefault(), { signal });
    this.addEventListener('click', (event) => {
      if (!suppressClick) return;
      suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, { capture: true, signal });
  }
  _onClick(event) {
    if (event.target.closest('[data-media-toggle]')?.getAttribute('aria-disabled') === 'true') return;
    if (event.target.closest('[data-media-toggle]')) {
      this._paused = !this._paused;
      this.toggleAttribute('media-paused', this._paused);
      this.querySelectorAll('video-media[autoplay]').forEach((media) => {
        this._paused || !this._current?.contains(media) ? media.pause() : media.play();
      });
      return;
    }
    const button = event.target.closest('.video-play-button');
    const slide = button && this.carousel?.items.find((item) => item.contains(button));
    if (slide) requestAnimationFrame(() => slide.querySelector('iframe, video')?.focus());
  }
}
if (!window.customElements.get('accessible-carousel')) {
  window.customElements.define('accessible-carousel', AccessibleCarousel);
}

class VariantFilter extends AccessibleCarousel {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('variant:change', this._onVariantChange.bind(this), { signal: this._abortController.signal });
  }
  get _items() {
    return Array.from(this.querySelectorAll('[data-variant-filter]'));
  }
  _matches(item, options) {
    const tokens = item.dataset.variantFilter.split(',').filter(Boolean);
    return tokens.length === 0 || tokens.some((token) => {
      const [position, value] = token.split(':');
      return (options[position - 1] || '').toLowerCase() === value;
    });
  }
  _onVariantChange(event) {
    const variant = event.detail.variant;
    const productId = event.target.elements?.['product-id']?.value;
    if (!variant?.options || productId !== this.getAttribute('product-id')) return;

    const items = this._items;
    const filteredIndexes = [];
    items.forEach((item, index) => {
      item.hidden = !this._matches(item, variant.options);
      if (item.hidden) filteredIndexes.push(index);
      item.querySelector('video-media')?.pause();
    });

    const visibleCount = items.length - filteredIndexes.length;
    this.hidden = visibleCount === 0;
    this.querySelectorAll('[data-variant-filter-controls] button').forEach((button) => {
      button.disabled = visibleCount < 2 || button.getAttribute('is') === 'prev-button';
    });
    const firstVisible = items.findIndex((item) => !item.hidden);
    this.querySelectorAll('page-dots').forEach((dots) => {
      dots.dispatchEvent(new CustomEvent('control:filter', { detail: { filteredIndexes } }));
      if (firstVisible >= 0) dots.select(firstVisible, false);
    });

    const carousel = this.querySelector('scroll-carousel');
    if (carousel && firstVisible >= 0) {
      carousel.select(firstVisible, { animate: false });
      this._setCurrent(items[firstVisible]);
    }
  }
}
if (!window.customElements.get('variant-filter')) {
  window.customElements.define('variant-filter', VariantFilter);
}

if (!window.pangolinHeaderKeyboard) {
  window.pangolinHeaderKeyboard = true;
  const hoverDetails = (target) => {
    const details = target.closest?.('details[is="dropdown-disclosure"], details[is="mega-menu-disclosure"]');
    return details && details.trigger === 'hover' ? details : null;
  };
  document.addEventListener('keydown', (event) => {
    const details = hoverDetails(event.target);
    if (!details) return;
    const summary = details.querySelector(':scope > summary');
    if (event.key === 'Escape' && details.open) {
      summary.focus();
      return;
    }
    if (event.target !== summary || !['Enter', ' ', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    if (event.key === 'ArrowDown') {
      details.open = true;
      details.lastElementChild.querySelector('a[href], button:not([disabled])')?.focus();
    } else {
      details.open = !details.open;
    }
  }, true);
  document.addEventListener('keyup', (event) => {
    if (event.key === ' ' && event.target.matches?.('summary') && hoverDetails(event.target)) event.preventDefault();
  }, true);
}

class ShopMegaMenu extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;
    const select = (event) => {
      const tab = event.target.closest('.shop-menu__category');
      if (tab) this.activate(this.tabs.indexOf(tab));
    };
    this.addEventListener('pointerover', select, { signal });
    this.addEventListener('focusin', select, { signal });
    this.addEventListener('keydown', this._onKeydown.bind(this), { signal });
    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        const panel = event.target.closest('.shop-menu__panel');
        if (panel) this.activate(this.panels.indexOf(panel));
      }, { signal });
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get tabs() {
    return Array.from(this.querySelectorAll('.shop-menu__category'));
  }
  get panels() {
    return Array.from(this.querySelectorAll('.shop-menu__panel'));
  }
  activate(index) {
    if (index < 0) return;
    this.tabs.forEach((tab, i) => tab.setAttribute('aria-current', i === index ? 'true' : 'false'));
    this.panels.forEach((panel, i) => panel.hidden = i !== index);
  }
  _onKeydown(event) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const tabs = this.tabs;
    const index = tabs.indexOf(event.target.closest('.shop-menu__category'));
    if (index < 0) return;
    event.preventDefault();
    tabs[(index + (event.key === 'ArrowDown' ? 1 : -1) + tabs.length) % tabs.length].focus();
  }
}
if (!window.customElements.get('shop-mega-menu')) {
  window.customElements.define('shop-mega-menu', ShopMegaMenu);
}
